import React, { useState, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { Pencil, Check, X } from 'lucide-react';
import { useToast } from './ui/toast';


const EditButton = ({ onClick, icon: Icon, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className="edit-button inline-flex items-center justify-center w-[16px] h-[16px] border border-gray-300"
    style={{
      padding: 0,
      minWidth: '24px',
      minHeight: '24px',
      backgroundColor: '#fff',
    }}
    aria-label="Edit"
  >
    <Icon size={12} strokeWidth={2} className={className || 'text-gray-600'} />
  </button>
);

EditButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.elementType.isRequired,
  className: PropTypes.string,
};

const StatementDetailsEditor = ({
  statement,
  onSave,
  showNeedsReview,
  showVerified,
  showManuallyVerified,
  onToggleNeedsReview,
  onToggleVerified,
  onToggleManuallyVerified,
  transactionCounts,
}) => {
  const { showToast } = useToast();
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [isFieldSaving, setIsFieldSaving] = useState(false);
  const confidenceThreshold = useSelector((state) => get(state, 'bankStatements.settings.confidenceThreshold'));

  const needsEdit = (confidence) => {
    if (confidenceThreshold === null) return false; // Don't show edit buttons while loading
    return !confidence || confidence < confidenceThreshold;
  };

  const handleEditClick = (field, currentValue) => {
    setEditingField(field);
    setTempValue(currentValue);
  };

  const validateDates = (field) => {
    const today = new Date();
    const threeYearAgo = new Date();
    threeYearAgo.setFullYear(today.getFullYear() - 3);

    if (field === 'statementStartDate' || field === 'statementEndDate') {
      const dateValue = new Date(tempValue);

      if (isNaN(dateValue)) {
        showToast('Invalid date format. Please enter a valid date.', 'error');
        return false;
      }

      if (dateValue > today) {
        showToast(`${field === 'statementStartDate' ? 'Start Date' : 'End Date'} cannot be greater than today.`, 'error');
        return false;
      }

      if (dateValue < threeYearAgo) {
        showToast(`${field === 'statementStartDate' ? 'Start Date' : 'End Date'} cannot be earlier than 3 years ago.`, 'error');
        return false;
      }

      if (field === 'statementEndDate' && statement.statementStartDate) {
        const startDate = new Date(statement.statementStartDate);
        if (dateValue < startDate) {
          showToast('End Date cannot be before Start Date.', 'error');
          return false;
        }
      }

      if (field === 'statementStartDate' && statement.statementEndDate) {
        const endDate = new Date(statement.statementEndDate);
        if (dateValue > endDate) {
          showToast('Start Date cannot be after End Date.', 'error');
          return false;
        }
      }
    }
    return true;
  }

  const handleSaveField = async (field) => {
    if (field === 'statementStartDate' || field === 'statementEndDate') {
      const isValid = validateDates(field);

      if (!isValid) {
        return;
      }
    }

    setIsFieldSaving(true);
    const updateData = {
      ...statement,
      [field]: tempValue,
      // Only update confidence for the field being edited
      bankNameConfidence:
        field === 'bankName' ? 0.5 : statement.bankNameConfidence || 0,
      bankAddressConfidence:
        field === 'bankAddress' ? 0.5 : statement.bankAddressConfidence || 0,
      accountHolderNameConfidence:
        field === 'accountHolderName'
          ? 0.5
          : statement.accountHolderNameConfidence || 0,
      accountHolderAddressConfidence:
        field === 'accountHolderAddress'
          ? 0.5
          : statement.accountHolderAddressConfidence || 0,
      accounts: statement.accounts.map(account => ({
        ...account,
        transactions: account.transactions.map(t => ({
          ...t,
          verificationStatus: t.verificationStatus,
        })),
      })),
    };

    await onSave(updateData, () => {
      setIsFieldSaving(false);
      setEditingField(null);
    });
  };

  const renderEditableField = (
    label,
    field,
    confidence
  ) => {
    const value = (statement[field] && statement[field].toString()) || '';
    const isEditing = editingField === field;
    const isDateField = field === 'statementStartDate' || field === 'statementEndDate';

    const formatDate = (dateString) => {
      if (!dateString) return '';
      // Split the date string and reconstruct it to avoid timezone issues
      const [year, month, day] = dateString.split('-');
      if (!year || !month || !day) return dateString;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day)
      ).toLocaleDateString();
    };

    return (
      <div className="detail-item">
        <div className="detail-label">{label}</div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
          }}
        >
          {isEditing ? (
            <Fragment>
              <div
                className={`detail-value flex-1 ${
                  isDateField ? 'date-input' : ''
                }`}
                style={{
                  overflow: 'hidden',
                }}
              >
                <input
                  type={isDateField ? 'date' : 'text'}
                  value={tempValue}
                  onChange={e => setTempValue(e.target.value)}
                  style={{
                    border: '1px solid #E5E7EB',
                    outline: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    height: '32px',
                  }}
                  max={
                    isDateField && field === 'statementStartDate'
                      ? statement.statementEndDate
                      : undefined
                  }
                  min={
                    isDateField && field === 'statementEndDate'
                      ? statement.statementStartDate
                      : undefined
                  }
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                {isFieldSaving ? (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Fragment>
                    <EditButton
                      onClick={() => handleSaveField(field)}
                      icon={Check}
                    />
                    <EditButton
                      onClick={() => setEditingField(null)}
                      icon={X}
                    />
                  </Fragment>
                )}
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div
                className="detail-value"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isDateField ? formatDate(value) : value}
              </div>
              {needsEdit(confidence) && (
                <div style={{ flexShrink: 0 }}>
                  <EditButton
                    onClick={() => handleEditClick(field, value)}
                    icon={Pencil}
                    className="text-red-400"
                  />
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="details-container">
      <div className="details-grid">
        {/* First Row - Two columns */}
        <div className="details-group">
          <h4 className="group-title">BANK INFORMATION</h4>
          {renderEditableField(
            'Bank Name',
            'bankName',
            statement.bankNameConfidence
          )}
          {renderEditableField(
            'Bank Address',
            'bankAddress',
            statement.bankAddressConfidence
          )}
        </div>

        <div className="details-group">
          <h4 className="group-title">STATEMENT PERIOD</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            {renderEditableField(
              'Start Date',
              'statementStartDate',
              statement.statementStartDateConfidence
            )}
            {renderEditableField(
              'End Date',
              'statementEndDate',
              statement.statementEndDateConfidence
            )}
          </div>
        </div>

        {/* Account Holder row */}
        <div className="details-group">
          <h4 className="group-title">ACCOUNT HOLDER</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            {renderEditableField(
              'Name',
              'accountHolderName',
              statement.accountHolderNameConfidence
            )}
          </div>
        </div>
        <div
          className="details-group"
          style={{ marginBottom: '1rem' }}
        >
          <h4 className="group-title">.</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            {renderEditableField(
              'Address',
              'accountHolderAddress',
              statement.accountHolderAddressConfidence
            )}
          </div>
        </div>
      </div>

      {/* Separate filter buttons section */}
      <div className="flex gap-2 mt-1">
        <button
          type="button"
          onClick={onToggleNeedsReview}
          className={`confidence-badge review ${
            showNeedsReview ? 'selected' : ''
          }`}
        >
          Needs Review (
          {transactionCounts.needsReview}
          )
        </button>
        <button
          type="button"
          onClick={onToggleVerified}
          className={`confidence-badge verified ${
            showVerified ? 'selected' : ''
          }`}
        >
          Auto Verified (
          {transactionCounts.verified}
          )
        </button>
        <button
          type="button"
          onClick={onToggleManuallyVerified}
          className={`confidence-badge manual ${
            showManuallyVerified ? 'selected' : ''
          }`}
          style={{
            backgroundColor: showManuallyVerified ? '#1d4ed8' : '#e0e7ff',
            color: showManuallyVerified ? 'white' : '#1e40af',
            borderColor: '#c1cae8',
            transition: 'all 0.2s ease',
          }}
        >
          Manually Verified (
          {transactionCounts.manuallyVerified}
          )
        </button>
      </div>
    </div>
  );
};

StatementDetailsEditor.propTypes = {
  statement: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  showNeedsReview: PropTypes.bool.isRequired,
  showVerified: PropTypes.bool.isRequired,
  showManuallyVerified: PropTypes.bool.isRequired,
  onToggleNeedsReview: PropTypes.func.isRequired,
  onToggleVerified: PropTypes.func.isRequired,
  onToggleManuallyVerified: PropTypes.func.isRequired,
  transactionCounts: PropTypes.object.isRequired,
};

export default StatementDetailsEditor;
